import { differenceInCalendarDays } from 'date-fns';

import {
  RequestBusinessPartnerStatusEnum,
  ServiceAgreementDeclineReasonEnum,
  ServiceAgreementStatusEnum,
  type RequestBusinessPartnerBody,
  type ServiceAgreementBody,
} from '../api/generated/pm';
import {
  SA_STATUS_PRIORITY,
  ServiceAgreement,
  ServiceAgreementExpiredEnum,
  ServiceAgreementStatus,
} from '../components/ServiceAgreement/Common/ServiceAgreementTypes';

export function computeServiceAgreementDerivedStatus(
  backendStatus: ServiceAgreementStatusEnum,
  requestExpiresAt?: Date | string
): { requestExpiresIn?: number; computedServiceAgreementStatus: ServiceAgreementStatus } {
  const requestExpiresIn = requestExpiresAt
    ? differenceInCalendarDays(new Date(requestExpiresAt), new Date())
    : undefined;

  const isPendingOrInvited =
    backendStatus === ServiceAgreementStatusEnum.PENDING ||
    backendStatus === ServiceAgreementStatusEnum.INVITED;

  const computed =
    isPendingOrInvited && requestExpiresIn !== undefined && requestExpiresIn < 0
      ? ServiceAgreementExpiredEnum.Expired
      : backendStatus;

  return { requestExpiresIn, computedServiceAgreementStatus: computed };
}

export function mapServiceAgreementBody(agreement: ServiceAgreementBody): ServiceAgreement {
  const { requestExpiresAt } = agreement;
  const { requestExpiresIn, computedServiceAgreementStatus } = computeServiceAgreementDerivedStatus(
    agreement.serviceAgreementStatus,
    requestExpiresAt
  );

  return {
    ...agreement,
    requestExpiresIn,
    computedServiceAgreementStatus,
  };
}

/**
 * Adapts a proxy `RequestBusinessPartnerBody` (Requests page, Create tab) to
 * the `ServiceAgreementBody` shape `mapServiceAgreementBody` and the shared
 * row cells expect. The result has no `customer.id`: a proxy is a request for
 * a Customer that does not exist yet.
 */
export function mapRequestBusinessPartnerToServiceAgreement(
  request: RequestBusinessPartnerBody
): ServiceAgreementBody {
  const mapStatus = (status: RequestBusinessPartnerStatusEnum): ServiceAgreementStatusEnum => {
    switch (status) {
      case RequestBusinessPartnerStatusEnum.ACCEPTED:
        return ServiceAgreementStatusEnum.ACTIVE;
      case RequestBusinessPartnerStatusEnum.CUSTOMER_REJECTED:
      case RequestBusinessPartnerStatusEnum.REJECTED:
        return ServiceAgreementStatusEnum.DECLINED;
      case RequestBusinessPartnerStatusEnum.DONE:
        return ServiceAgreementStatusEnum.ACTIVE;
      case RequestBusinessPartnerStatusEnum.CLOSED:
        return ServiceAgreementStatusEnum.DECLINED;
      case RequestBusinessPartnerStatusEnum.PENDING:
      case RequestBusinessPartnerStatusEnum.CUSTOMER_REVIEW:
      default:
        return ServiceAgreementStatusEnum.PENDING;
    }
  };

  return {
    id: request.id,
    createdAt: request?.createdAt || new Date(),
    modifiedAt: request?.modifiedAt || new Date(),
    serviceAgreementStatus: mapStatus(request.status),
    serviceAgreementTerms: request.serviceAgreementTerms,
    providerBusinessPartnerId: request.providerBusinessPartnerId,
    provider: request.provider
      ? {
          id: request.provider.id,
          name: request.provider.name,
          companyAddress: request.provider.companyAddress || {
            addressLine1: '',
            city: '',
            country: '',
            zipPostalCode: '',
          },
        }
      : undefined,
    activeSince:
      request.status === RequestBusinessPartnerStatusEnum.ACCEPTED ||
      request.status === RequestBusinessPartnerStatusEnum.DONE
        ? request.createdAt
        : undefined,
    requestExpiresAt: request.expiresAt,
    providerCustomerNumber: undefined,
    declineReason:
      request.status === RequestBusinessPartnerStatusEnum.REJECTED
        ? ServiceAgreementDeclineReasonEnum.REASON_OTHER
        : undefined,
    customer: {
      name: request.name,
      companyAddress: request.companyAddress,
    },
  } as ServiceAgreementBody;
}

export function getServiceAgreementStatusPriority(status: ServiceAgreementStatus): number {
  return SA_STATUS_PRIORITY.get(status) ?? 99;
}
